/** @jsxImportSource @emotion/react */
import { useContext, useEffect, useRef } from 'react';

import useTranslate from '@common/hooks/useTranslate';
import { sendGAEvent } from '@common/utlis';
import Accordion from '@stories/atoms/Accordion';
import Button from '@stories/atoms/Button';
import CloseBigIcon from '@stories/atoms/Icons/close_big.svg';
import CategoryTree from '@stories/templates/Search/CategoryTree';
import { media, mediaDown } from '@stories/theming/settings';
import { getNumberOfActiveFilters, useProductListContext } from '@utils/productList';

import { PageContext } from '../../Context/pageContext';

import SectionCheckboxes from './SectionCheckboxes/SectionCheckboxes';
import SectionRadioButton, {
  SectionPrice,
  SectionRatings,
  SectionSorting
} from './SectionRadioButtons/SectionRadioButtons';

import type { Theme } from '@emotion/react';
import type { Interpolation } from '@emotion/serialize';

const FilterSectionTypes = {
  averageRating: SectionRatings,
  checkbox: SectionCheckboxes,
  price_range: SectionPrice,
  radio: SectionRadioButton,
  sort: SectionSorting
};

const translations = {
  Distinction: { de_DE: 'Qualität', nl_NL: '' },
  offer: { de_DE: 'Angebot', nl_NL: '' },
  'Dosage Form': { de_DE: 'Darreichungsform', nl_NL: '' },
  Diet: { de_DE: 'Ernährungsform', nl_NL: '' },
  Price: { de_DE: 'Preis', nl_NL: '' },
  'Age Group': { de_DE: 'Altersgruppe', nl_NL: '' },
  Content: { de_DE: 'Inhalt', nl_NL: '' },
  filters: { de_DE: 'Filter${activeFilterCount}', nl_NL: '' },
  Flavor: { de_DE: 'Geschmacks', nl_NL: '' },
  'Free of': { de_DE: 'Frei von', nl_NL: '' },
  'Product Size': { de_DE: 'Produktgröße', nl_NL: '' },
  Manufacturer: { de_DE: 'Marke', nl_NL: '' },
  Ratings: { de_DE: 'Bewertungen', nl_NL: '' },
  Sorting: { de_DE: 'Sortierung', nl_NL: '' },
  'show products': { de_DE: '${total} produkte anzeigen' }
};

const containerClass = {
  [mediaDown('desktop')]: {
    margin: '0.5rem -0.5rem 0',
    paddingBottom: '7rem',
    maxHeight: '100%',
    overflow: 'auto',
    width: 'calc(100% + 1rem)'
  }
};

const ctaClass = {
  backgroundColor: 'white',
  borderTop: 'solid thin var(--color-border-grey)',
  bottom: '0',
  display: 'flex',
  justifyContent: 'center',
  left: '0',
  padding: '1rem',
  position: 'fixed',
  width: '100%',
  [media('desktop')]: {
    display: 'none'
  }
} as const;

export const isValidOption = ({ count, label }) =>
  typeof count === 'number' && count > 0 && typeof label === 'string' && label.length > 0;

type TFiltersProps = {
  cssProps?: Interpolation<Theme>;
};
const Filters: React.FC<TFiltersProps> = ({ cssProps }) => {
  const translate = useTranslate(translations);
  const { categoryTree, hideOverlay, isFilterOverlayShown, setFilterOverlayShown } = useContext(PageContext);
  const { currentCategoryId } = useProductListContext();
  const {
    activeFiltersAndSorting: { activeFilters },
    defaultSettings: { sortingOptions },
    productsAndAvailableFilters: { total, filterList }
  } = useProductListContext();
  const filterContainerRef = useRef<HTMLDivElement>(null);

  const filterMobileConfiguration = {
    name: 'sort',
    configuration: {
      title: 'Sorting',
      type: 'radio'
    },
    options: sortingOptions
  };

  useEffect(() => {
    if (!isFilterOverlayShown) {
      filterContainerRef.current && filterContainerRef.current.scrollTo(0, 0);
    }
  }, [isFilterOverlayShown]);

  return (
    <div css={() => ({ [media('desktop')]: { opacity: '.85' }, ...(cssProps as object) })}>
      <h3
        css={(t) => ({
          alignItems: 'center',
          display: 'flex',
          justifyContent: 'space-between',
          margin: '0',
          marginLeft: 'var(--offset)',
          marginRight: 'var(--offset)',
          ...t.headline.h3,
          [media('desktop')]: {
            display: 'none'
          }
        })}
      >
        {translate('filters', {
          activeFilterCount:
            getNumberOfActiveFilters(activeFilters) > 0 ? ` (${getNumberOfActiveFilters(activeFilters)})` : ''
        })}
        <button
          type="button"
          onClick={() => {
            setFilterOverlayShown(false);
            hideOverlay();
          }}
        >
          <CloseBigIcon css={{ path: { fill: 'hsl(0, 0%, 40%)' } }} />
        </button>
      </h3>
      <div css={containerClass} ref={filterContainerRef}>
        {currentCategoryId && (
          <CategoryTree
            categoryTree={categoryTree}
            cssProps={{ [mediaDown('desktop')]: { display: 'none' } }}
            currentCategoryId={currentCategoryId}
          />
        )}
        {[
          filterMobileConfiguration,
          ...filterList.filter(({ options }) => options.filter(isValidOption).length > 0)
        ].map(({ configuration: { type, title }, ...filterSection }, index) => {
          const FilterSectionComponent =
            FilterSectionTypes[filterSection.name] || FilterSectionTypes[type] || SectionCheckboxes;
          return (
            <div
              css={{
                borderBottom: 'thin solid var(--color-border-grey)',
                marginLeft: 'var(--offset)',
                marginRight: 'var(--offset)',
                [media('desktop')]: {
                  margin: 0
                },
                ...(filterSection.name === 'sort' && {
                  [media('desktop')]: { display: 'none' }
                })
              }}
              key={title}
              id={`filter_section_${title}`}
            >
              <Accordion
                title={translate(title)}
                content={<FilterSectionComponent {...{ filterSection }} />}
                trackClick={(open) =>
                  sendGAEvent({
                    category: 'Search Filter',
                    action: open ? 'Section closed' : 'Section opened',
                    label: title
                  })
                }
                open={index < 9}
              />
            </div>
          );
        })}
      </div>
      <div css={ctaClass}>
        <Button
          label={translate('show products', { total })}
          buttonStyle="primary"
          size="big"
          cssProps={{
            margin: 'auto',
            maxWidth: '420px',
            width: '100%'
          }}
          attrs={{ onClick: () => hideOverlay() }}
        />
      </div>
    </div>
  );
};

export default Filters;
